// Utils & misc
import cn from '../../../utils/cn';

// Components
import Tag from '../../atoms/Tag/Tag';

// Style
import style from './DetailRow.module.css';

/**
 * DetailRow component - Displays a label on the left and a value on the right
 * Without label, the value spans both columns
 * @param {object} params
 * @param {string} params.label - Label text displayed on the left
 * @param {string|React.ReactNode} params.value - Value displayed on the right
 * @param {'text'|'tag'} [params.type='text'] - Type of value display
 * @param {'primary'|'neutral'|'success'|'alert'|'yellow'|'orange'|'green'|'blue'} [params.tagTheme='primary'] - Theme for tag type
 * @param {boolean} [params.fullWidth=false] - Span both columns (no label column)
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} DetailRow component
 */
function DetailRow({
  label,
  value,
  type = 'text',
  tagTheme = 'primary',
  fullWidth = false,
  className,
}) {
  const renderValue = () => {
    switch (type) {
      case 'tag':
        return (
          <Tag theme={tagTheme} className={style.tag}>
            {value}
          </Tag>
        );
      case 'text':
      default:
        return <span className={style['text-value']}>{value}</span>;
    }
  };

  if (fullWidth) {
    return (
      <tr className={cn([style['detail-row'], style['detail-row-full-width'], className])}>
        <td colSpan={2} className={style['full-width-value']}>
          {value}
        </td>
      </tr>
    );
  }

  return (
    <tr className={cn([style['detail-row'], className])}>
      {label ? <td className={style.label} title={label}>{label}</td> : null}
      <td className={style.value} colSpan={label ? undefined : 2}>{renderValue()}</td>
    </tr>
  );
}

/**
 * DetailRowTable component - Displays a table of detail rows with items loop
 * @param {object} params
 * @param {Array<any>} params.items - Array of items to display
 * @param {Function} params.children - Render function that receives an item and returns DetailRow props
 * @param {string} [params.className] - Additional CSS classes
 * @returns {JSX.Element} DetailRowTable component
 */
export function DetailRowTable({
  items, children, className,
}) {
  return (
    <table className={cn([style['detail-row-table'], className])}>
      {/* Fixed column widths, whatever the first row (fixed table layout) */}
      <colgroup>
        <col className={style['label-column']} />
        <col />
      </colgroup>
      <tbody>
        {items?.length > 0
          ? items.map((item, index) => children(item, index))
          : null}
      </tbody>
    </table>
  );
}

export default DetailRow;
